import { ROIClientDTO, TFocusLevelImage } from './../../business/models/CustomModels';
import { createSlice, PayloadAction, prepareAutoBatched } from '@reduxjs/toolkit';
import { DrawingLayer } from 'business/models/DrawingLayer';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { ImageTuningValues } from 'business/models/ImageTuningModel';
import { CurrentWellDetail } from 'business/types';
import { SelectedDrawingDetails } from 'components/plateviewer/well/DrawingManagerRenderer';
import { AnnotationsDTO, PlateDropRegions } from 'business/models/Models';
import { wellImagesFetchedData } from 'components/plateviewer/well/WellImage/WellImage';
import { CrystalClientDTO } from 'components/Experiment/constants';
import { AnnotationPreference } from 'components/commandpanel/ExperimentPanel/ExperimentTools/AnnotationTools';

interface AppState extends ExperimentDtoClient {
  generateReqd: number;
  expData: any;
  dropImageByKey: Record<string, TFocusLevelImage>;
  selectedDrawing: SelectedDrawingDetails;
  isEditableActive: boolean;
  expHitWells: any[];
  hitButtonName: string;
  expSaved: number;
  annotations: any[];
  rulers: any;
  scribbles: any[];
  rois: any[];
  pois: any[];
  crystals: any[];
  isMoveToCompareTab: boolean;
  product: any;
  isWellImagingEnabled: boolean;
  isRegionImaging: boolean;
  drawingColor: any;
  wellDropRegions: PlateDropRegions[];
  plateDrawings: AnnotationsDTO;
  selectedIngredients: string[];
  checkedCrystalIDs: number[];
  preFetchedWellImages: wellImagesFetchedData[];
  annotationPreference: AnnotationPreference;
  plateDisabledImageRegions: PlateDropRegions[];
  globaldataloaded: boolean;
  navigatorImagesAvailableKeys: string[];
}

const initialState = {
  generateReqd: 0,
  expId: -1,
  layers: [],
  dropImageByKey: {},
  product: { edition: '' },
  isMoveToCompareTab: false,
  drawingColor: { r: 74, g: 144, b: 226 },
  wellDropRegions: [],
  plateDrawings: null,
  selectedIngredients: [],
  expSaved: 0,
  checkedCrystalIDs: [],
  crystals: [],
  preFetchedWellImages: [],
  annotationPreference: null,
  plateDisabledImageRegions: [],
  hideScaleBar: {},
  hideOverlays: {},
  globaldataloaded: false,
  navigatorImagesAvailableKeys: [],
} as unknown as AppState;

export const exprimentSlice = createSlice({
  name: 'experiment',
  initialState,
  reducers: {
    setSelectedCell: (state, action: PayloadAction<CurrentWellDetail>): void => {
      state.selectedCell = action.payload;
    },
    setGlobalDataLoaded: (state, action: PayloadAction<boolean>): void => {
      state.globaldataloaded = action.payload;
    },
    setImageTuning: (state, action: PayloadAction<ImageTuningValues>): void => {
      state.imageTuningValues = action.payload;
    },
    setLayersInfo: {
      reducer: (state, action: PayloadAction<DrawingLayer[]>) => {
        state.layers = [...action.payload];
      },
      prepare: prepareAutoBatched<DrawingLayer[]>(),
    },
    callGenerateLayer: (state, action: PayloadAction<number>) => {
      state.generateReqd++;
    },
    resetGenerateLayer: (state, action: PayloadAction<number>) => {
      state.generateReqd = 0;
    },
    setExpDataUpdated: (state, action: PayloadAction<string>) => {
      state.expData = action.payload;
    },
    setDropImageForKey: (
      state,
      action: PayloadAction<{ key: string; image: TFocusLevelImage }>,
    ) => {
      const { key, image } = action.payload;
      state.dropImageByKey[key] = image;
    },
    setSelectedDrawing: (state, action: PayloadAction<SelectedDrawingDetails>): any => {
      return { ...state, selectedDrawing: action.payload };
    },
    setIsEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEditableActive = action.payload;
    },
    setExpHitWell: (state, action: PayloadAction<any[]>) => {
      state.expHitWells = action.payload;
    },
    setHitButtonName: (state, action: PayloadAction<string>) => {
      state.hitButtonName = action.payload;
    },
    setExpSaved: (state) => {
      state.expSaved++;
    },
    setAnnotationsDetail: (
      state,
      action: PayloadAction<{ dropNumber: number; annotations: any[] }>,
    ) => {
      const { dropNumber, annotations } = action.payload;
      return {
        ...state,
        annotations: {
          ...state.annotations,
          [dropNumber]: annotations,
        },
      };
    },
    setRulersDetail: (
      state,
      action: PayloadAction<{ dropNumber: number; rulers: any[] }>,
    ) => {
      const { dropNumber, rulers } = action.payload;
      return {
        ...state,
        rulers: {
          ...state.rulers,
          [dropNumber]: rulers,
        },
      };
    },
    setScribblesDetail: (
      state,
      action: PayloadAction<{ dropNumber: number; scribbles: any[] }>,
    ) => {
      const { dropNumber, scribbles } = action.payload;
      return {
        ...state,
        scribbles: {
          ...state.scribbles,
          [dropNumber]: scribbles,
        },
      };
    },
    setIsMoveToCompareTab: (state, action: PayloadAction<boolean>) => {
      state.isMoveToCompareTab = action.payload;
    },
    setROIDetail: (state, action: PayloadAction<{ dropNumber: number; rois: any[] }>) => {
      const { dropNumber, rois } = action.payload;
      return {
        ...state,
        rois: {
          ...state.rois,
          [dropNumber]: rois,
        },
      };
    },
    setPOIDetail: (state, action: PayloadAction<{ dropNumber: number; pois: any[] }>) => {
      const { dropNumber, pois } = action.payload;
      return {
        ...state,
        pois: {
          ...state.pois,
          [dropNumber]: pois,
        },
      };
    },
    setCrystalsDetail: (
      state,
      action: PayloadAction<{ dropNumber: number; crystals: any[] }>,
    ) => {
      const { dropNumber, crystals } = action.payload;
      return {
        ...state,
        crystals: {
          ...state.crystals,
          [dropNumber]: crystals,
        },
      };
    },
    setProductEdition: (state, action: PayloadAction<string>) => {
      state.product.edition = action.payload;
    },
    setIsWellImagingEnabled: (state, action: PayloadAction<boolean>) => {
      state.isWellImagingEnabled = action.payload;
    },
    setIsRegionImaging: (state, action: PayloadAction<boolean>) => {
      state.isRegionImaging = action.payload;
    },
    setDrawingColor: (state, action: PayloadAction<any>) => {
      state.drawingColor = action.payload;
    },
    setWellDropRegions: (state, action: PayloadAction<PlateDropRegions[]>) => {
      state.wellDropRegions = action.payload;
    },
    setAndAppendWellDropRegions: (
      state,
      action: PayloadAction<{ regions: PlateDropRegions[]; plateId: number }>,
    ) => {
      const newState = state.wellDropRegions?.filter(
        (x) => x.plateId !== action.payload.plateId,
      );
      newState?.push(...action.payload.regions);
      state.wellDropRegions = newState;
    },
    setPlateDrawings: (state, action: PayloadAction<AnnotationsDTO>) => {
      state.plateDrawings = action.payload;
    },
    setPlateDisabledImageRegions: (state, action: PayloadAction<PlateDropRegions[]>) => {
      state.plateDisabledImageRegions = action.payload;
    },
    setAndAppendPlateDisabledImageRegions: (
      state,
      action: PayloadAction<{
        plateDisabledImageRegions: PlateDropRegions[];
        plateIdToUpdate: number;
      }>,
    ) => {
      const { plateDisabledImageRegions, plateIdToUpdate } = action.payload;
      let newState = state.plateDisabledImageRegions?.filter(
        (x) => x.plateId !== plateIdToUpdate,
      );
      if (newState) {
        newState?.push(...plateDisabledImageRegions);
      } else {
        newState = plateDisabledImageRegions;
      }
      state.plateDisabledImageRegions = newState;
    },
    setToggleDisableImages: (
      state,
      action: PayloadAction<{ selectedRegionIDs: number[]; toggleState: boolean }>,
    ) => {
      const { selectedRegionIDs, toggleState } = action.payload;
      const allRegions = state.plateDisabledImageRegions.map((region) => {
        if (selectedRegionIDs.includes(region.regionId)) {
          region.imagingEnabled = toggleState;
        }
        return region;
      });
      state.plateDisabledImageRegions = allRegions;
    },
    setSelectedIngredientList: (state, action: PayloadAction<string[]>) => {
      state.selectedIngredients = action.payload;
    },
    setCheckedCrystalIDs: (state, action: PayloadAction<number[]>): void => {
      state.checkedCrystalIDs = action.payload;
    },

    setRoiDrawing: (
      state,
      action: PayloadAction<{
        roiToAdd: ROIClientDTO;
        tempRoiID: number;
        dropNumber: number;
      }>,
    ): void => {
      const { roiToAdd, tempRoiID, dropNumber } = action.payload;
      state.rois[dropNumber] = state.rois[dropNumber].map((roi) => {
        if (roi.id < 0 && roi.id === tempRoiID) {
          return roiToAdd;
        }
        return roi;
      });
    },
    setPreFetchedWellImages: (
      state,
      action: PayloadAction<wellImagesFetchedData[]>,
    ): void => {
      state.preFetchedWellImages = action.payload;
    },
    appendPreFetchedWellImages: (
      state,
      action: PayloadAction<wellImagesFetchedData>,
    ): void => {
      const wellDetails = action.payload.wellDetails;
      const updatedPreFetchedWellImages = state.preFetchedWellImages?.filter(
        (x) =>
          x.wellDetails.wellNum !== wellDetails.wellNum ||
          x.wellDetails.cpID !== wellDetails.cpID ||
          x.wellDetails.dropNumber !== wellDetails.dropNumber ||
          x.wellDetails.inspectionSet?.id !== wellDetails.inspectionSet?.id ||
          x.wellDetails.regionID !== wellDetails.regionID ||
          x.wellDetails.plateID !== wellDetails.plateID,
      );
      updatedPreFetchedWellImages.push(action.payload);
      state.preFetchedWellImages = updatedPreFetchedWellImages;
    },
    setClearPreFetchedWellImages: (state): void => {
      state.preFetchedWellImages = [];
    },
    setRemovePreFetchedWellImage: (state, action: PayloadAction<number>): void => {
      const wellNumberToRemove = action.payload;
      const updatedPreFetchedWellImages = { ...state.preFetchedWellImages };
      delete updatedPreFetchedWellImages[wellNumberToRemove];
      state.preFetchedWellImages = updatedPreFetchedWellImages;
    },
    setReplacePreFetchedTunedImage: (state, action: PayloadAction<any>): void => {
      const { wellDetails, tuningValues, tunedImage } = action.payload;
      const updatedPreFetchedWellImages = [...state.preFetchedWellImages];
      const imageToUpdate = updatedPreFetchedWellImages?.find(
        (x) =>
          x.wellDetails.wellNum == wellDetails.wellNum &&
          x.wellDetails.cpID == wellDetails.cpID &&
          x.wellDetails.dropNumber == wellDetails.dropNumber &&
          x.wellDetails.inspectionSet?.id == wellDetails.inspectionSet?.id,
      );
      imageToUpdate.imageData.tunedImage = tunedImage;
      imageToUpdate.imageData.tuningValues = tuningValues;
      state.preFetchedWellImages = updatedPreFetchedWellImages;
    },
    addNewCrystal: (
      state,
      action: PayloadAction<{ dropNumber: number; crystals: any[] }>,
    ) => {
      const { dropNumber, crystals } = action.payload;
      const updatedCrystals = crystals.map((newCrystal) => {
        const matchiningCrystal = state.crystals[dropNumber]?.find(
          (x) => x.id === newCrystal.id,
        );
        if (matchiningCrystal) {
          return {
            ...newCrystal,
            locked: matchiningCrystal.locked,
          } as CrystalClientDTO;
        }
        return newCrystal as CrystalClientDTO;
      });
      state.crystals[dropNumber] = updatedCrystals;
    },
    updateScribblePoints: (
      state,
      action: PayloadAction<{ points: number[]; dropNumber: number }>,
    ) => {
      const { points, dropNumber } = action.payload;
      if (state.scribbles?.[dropNumber]?.length) {
        const scribbleToUpdate =
          state.scribbles[dropNumber][state.scribbles[dropNumber].length - 1];
        if (!scribbleToUpdate.points) {
          scribbleToUpdate.points = points;
        } else {
          scribbleToUpdate.points = scribbleToUpdate.points.concat(points);
        }
      }
    },
    setAnnotationPreference: (state, action: PayloadAction<AnnotationPreference>) => {
      state.annotationPreference = action.payload;
    },
    appendNavigatorImagesAvailableKeys: (state, action: PayloadAction<string>) => {
      const navigatorImagesKey = action.payload;
      state.navigatorImagesAvailableKeys.push(navigatorImagesKey);
    },
  },
});

export const {
  setSelectedCell,
  setGlobalDataLoaded,
  setImageTuning,
  setLayersInfo,
  callGenerateLayer,
  setExpDataUpdated,
  setDropImageForKey,
  resetGenerateLayer,
  setSelectedDrawing,
  setIsEditMode,
  setExpHitWell,
  setHitButtonName,
  setExpSaved,
  setAnnotationsDetail,
  setRulersDetail,
  setScribblesDetail,
  setIsMoveToCompareTab,
  setROIDetail,
  setPOIDetail,
  setCrystalsDetail,
  setProductEdition,
  setIsWellImagingEnabled,
  setIsRegionImaging,
  setDrawingColor,
  setWellDropRegions,
  setAndAppendWellDropRegions,
  setPlateDrawings,
  setSelectedIngredientList,
  setCheckedCrystalIDs,
  setRoiDrawing,
  setPreFetchedWellImages,
  setClearPreFetchedWellImages,
  setRemovePreFetchedWellImage,
  setReplacePreFetchedTunedImage,
  appendPreFetchedWellImages,
  addNewCrystal,
  updateScribblePoints,
  setAnnotationPreference,
  setAndAppendPlateDisabledImageRegions,
  setToggleDisableImages,
  appendNavigatorImagesAvailableKeys,
} = exprimentSlice.actions;

export default exprimentSlice.reducer;
